import { SkipLinks } from '@greystar/designsystem';
import { AnalyticsProvider, getWindowLocation, useAnalytics } from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import Head from 'next/head';
import React from 'react';
import { PrimaryLayoutProps, RouteFields } from './PrimaryLayout.types';

// Create a separate component that uses the analytics hook
const PrimaryLayoutContent = ({
  layoutData,
  t,
}: {
  layoutData: PrimaryLayoutProps['layoutData'];
  headLinks: PrimaryLayoutProps['headLinks'];
  t: (key: string) => string;
}) => {
  const { route, context } = layoutData.sitecore;
  const analytics = useAnalytics();
  const property: EventProperty = context?.property ?? {};
  const location = property?.location ?? {};

  const addressParts = [
    location?.address,
    location?.city,
    location?.state,
    location?.postalCode,
  ].filter(Boolean);
  const property_location = addressParts.join(', ');

  const handleSkipLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const currentUrl = getWindowLocation('href');
    const eventData = {
      event: 'web_accessibility_skip',
      action_type: 'skip to main content',
      state: location?.state ?? '',
      property_location: property_location,
      property_id: property?.id ?? '',
      property_name: property?.name ?? '',
      module_name: 'accessibility',
      inpage_location: 'top',
      click_text: t('skip_link_label') ?? 'Skip to main content',
      click_type: 'internal',
      destination_url: currentUrl ?? '',
    };

    analytics.trackEvent('web_accessibility_skip', eventData);
  };

  return (
    <>
      <SkipLinks
        label={t('skip_link_label') || 'Skip to main content'}
        target="#main_content"
        onClick={handleSkipLinkClick}
      />
      <header>
        <div id="header">{route && <Placeholder name="headless-header" rendering={route} />}</div>
      </header>
      <main id="main_content">
        <div id="content">{route && <Placeholder name="headless-main" rendering={route} />}</div>
      </main>
      <footer>
        <div id="footer">{route && <Placeholder name="headless-footer" rendering={route} />}</div>
      </footer>
    </>
  );
};

export const PrimaryLayout = ({ layoutData, headLinks }: PrimaryLayoutProps): JSX.Element => {
  const { t } = useI18n();
  const { route } = layoutData.sitecore;
  const fields = route?.fields as RouteFields;

  return (
    <>
      <Head>
        <link rel="icon" href={`/favicon.ico`} />
        <link rel="preconnect" href="https://s7d9.scene7.com/" />
        {fields.metaTitle?.value && <title>{fields?.metaTitle?.value}</title>}
        {fields.metaDescription?.value && (
          <meta name="description" content={fields.metaDescription.value} />
        )}
        {fields.metaRobots?.value && <meta name="robots" content={fields.metaRobots.value} />}
        {headLinks.map((headLink) => (
          <link rel={headLink.rel} key={headLink.href} href={headLink.href} />
        ))}
      </Head>
      <AnalyticsProvider sitecoreData={layoutData.sitecore}>
        <PrimaryLayoutContent layoutData={layoutData} headLinks={headLinks} t={t} />
      </AnalyticsProvider>
    </>
  );
};
